/*eslint max-lines-per-function: ["error", 255]*/
import React, { useEffect, useState } from 'react';
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import * as Icon from 'react-bootstrap-icons';

import { showMessageDialog } from '../../../common/messageDialog';
import { useAuth } from '../../../common/hooks/useAuth';
import { useSzafir } from '../../../common/hooks/useSzafir';
import { useSpdCore } from '../../../common/hooks/useSpdCore';
import { Role } from '../../../common/spdCore/authorization/roles';
import { SignatureStatus } from '../../../common/spdCore/validation/schemas';
import { responseErrors } from '../../../common/spdCore/validation/responseErrors';
import { SigningInProgressModal } from '../Szafir/SigningInProgressModal';
import { sendJudgmentToP1, signP1Document } from '../Szafir/sendJudgmentToP1';
import { signJudgmentPdfWithSzafir } from '../Szafir/signJudgmentPdfWithSzafir';
import { ResponseError } from '../../../common/spdCore/autogenerated/spdApiClient';
import { SimP1StatusEnum } from '../../../common/spdCore/validation/enums';

import { SimP1StatusBadge } from './SimP1StatusBadge';

export const JudgmentSignLink = (props: {
  judgmentId: string;
  signStatus?: string | null | undefined;
  sendToP1?: boolean;
  statusP1?: SimP1StatusEnum | null | undefined;
  cancelStatusP1?: SimP1StatusEnum | null | undefined;
  psychologistId?: string | null;
}) => {
  const auth = useAuth();
  const szafir = useSzafir();
  const spd = useSpdCore();
  const [signStatus, setSignStatus] = useState<string>(props.signStatus || '');
  const canSignCancellation =
    auth.canSign &&
    !!auth.check([Role.MedicalAssessor]) &&
    !!props.psychologistId &&
    props.psychologistId === auth.currentUser?.uid;
  const [signingInProgress, setSigningInProgress] = useState<boolean>(false);
  const [statusP1, setStatusP1] = useState(props.statusP1);
  const [cancelStatusP1, setCancelStatusP1] = useState(props.cancelStatusP1);

  useEffect(() => {
    setCancelStatusP1(props.cancelStatusP1);
  }, [props.cancelStatusP1]);
  useEffect(() => {
    setSignStatus(props.signStatus || '');
  }, [props.signStatus]);

  const handleClick = async () => {
    if (!auth.userProfile?.pin) {
      showMessageDialog({
        title: 'Brak zdefiniowanego PIN-u',
        message:
          'Aby podpisać orzeczenie, musisz najpierw zdefiniować swój PIN.\n\n' +
          'Co zrobić:\n' +
          'Kliknij w ikonę użytkownika w prawym górnym rogu, a następnie wybierz "Zapisz PIN".\n' +
          'Po zapisaniu PIN-u spróbuj ponownie.',
        variant: 'warning',
      });
      return;
    }
    setSigningInProgress(true);
    const judgment = await spd.judgments.get(props.judgmentId);
    await signSaveAndSendEmail();
    if (judgment?.article?.engPdfTemplateType) {
      await new Promise(f => setTimeout(f, 6000));
      await signSaveAndSendEmail(true);
    }
  };

  const signSaveAndSendEmail = async (english = false) => {
    spd.szafir
      .getTaskListForJudgment(props.judgmentId, english)
      .then(async taskList => {
        await szafir.signDocument(
          taskList,
          auth.userProfile?.pin || '',
          props.judgmentId,
          '',
          english,
          saveSignedPdfAndSendEmail,
          signedFailed,
        );
      })
      .catch(async response => {
        const errors = await responseErrors(response as ResponseError);
        showMessageDialog({
          title: 'Błąd podpisu orzeczenia',
          message:
            'Nie udało się przygotować dokumentu do podpisu.\n\n' +
            'Szczegóły: ' +
            errors,
          variant: 'danger',
        });
        setSigningInProgress(false);
      });
  };

  const saveSignedPdfAndSendEmail = async (
    result: Buffer | null,
    documentId: string,
    documentNumber: string,
    english: boolean,
  ) => {
    await spd.savedPdfs
      .savePdfFileForJudgment({
        entityId: props.judgmentId,
        file: result,
        english: english,
      })
      .then(() => {
        setSignStatus(SignatureStatus.Signed);
        setSigningInProgress(false);
        openSignedDocument(result);
      })
      .then(async () => {
        await spd.emails.sendJudgmentByEmail(props.judgmentId, english);
      })
      .catch(async response => {
        const errors = await responseErrors(response as ResponseError);
        alert('Błąd zapisu PDF do bazy danych:\n ' + errors);
        setSigningInProgress(false);
      });
  };

  const signedFailed = (error: string) => {
    showMessageDialog({
      title: 'Błąd podpisu orzeczenia',
      message: 'Podpisywanie nie powiodło się.\n\nSzczegóły: ' + error,
      variant: 'danger',
    });
    setSigningInProgress(false);
  };

  const handleSendToP1 = async () => {
    try {
      setSigningInProgress(true);
      const judgment = await spd.judgments.get(props.judgmentId);
      await spd.pdfPrinter.saveWithoutPrintJudgment(props.judgmentId);
      await signJudgmentPdfWithSzafir(
        szafir,
        spd,
        auth.userProfile?.pin || '',
        props.judgmentId,
        judgment.number || '',
      );
      setSignStatus(SignatureStatus.Signed);
      if (judgment?.article?.engPdfTemplateType) {
        await new Promise(f => setTimeout(f, 6000));
        await signJudgmentPdfWithSzafir(
          szafir,
          spd,
          auth.userProfile?.pin || '',
          props.judgmentId,
          judgment.number || '',
          true,
        );
      }
      if (auth.userProfile?.signP1WithSzafir) {
        await new Promise(f => setTimeout(f, 6000));
      }
      await sendJudgmentToP1(auth, szafir, spd, props.judgmentId);
      setStatusP1(SimP1StatusEnum.WaitingForP1);
      setSigningInProgress(false);
    } catch (response) {
      const errors = await responseErrors(response as ResponseError);
      alert('Błąd wysyłania do SIM:\n ' + errors);
      setSigningInProgress(false);
    }
  };

  const handleSignCancellation = async () => {
    try {
      setSigningInProgress(true);
      const { id: documentId } =
        await spd.judgmentP1Documents.getLastForJudgment(props.judgmentId);
      await signP1Document(auth, szafir, spd, documentId);
      setCancelStatusP1(SimP1StatusEnum.WaitingForP1);
    } catch (response) {
      const errors = await responseErrors(response as ResponseError);
      alert('Błąd podpisywania anulowania w SIM:\n ' + errors);
    }
    setSigningInProgress(false);
  };

  const openSignedDocument = (pdfData: Buffer | null) => {
    if (!pdfData) {
      return;
    }
    const blob = new Blob([new Uint8Array(pdfData)], {
      type: 'application/pdf',
    });
    const blobURL = URL.createObjectURL(blob);
    window.open(blobURL);
  };

  if (signingInProgress) {
    return <SigningInProgressModal />;
  }

  return (
    <div className="text-center">
      {signStatus !== SignatureStatus.Unnecessary &&
        props.sendToP1 &&
        (statusP1 === null ||
          statusP1 === SimP1StatusEnum.ToSign ||
          statusP1 === SimP1StatusEnum.ValidationFailed) && (
          <OverlayTrigger
            overlay={
              <Tooltip style={{ position: 'fixed' }}>
                Podpisz i Wyślij do SIM
              </Tooltip>
            }
          >
            <Button
              onClick={e => {
                e.stopPropagation();
                void handleSendToP1();
              }}
              variant="btn-info"
            >
              <Icon.SendCheck className="icon-red" />
            </Button>
          </OverlayTrigger>
        )}
      {canSignCancellation && cancelStatusP1 === SimP1StatusEnum.ToSign && (
        <OverlayTrigger
          overlay={
            <Tooltip style={{ position: 'fixed' }}>
              Podpisz anulowanie w SIM
            </Tooltip>
          }
        >
          <Button
            onClick={e => {
              e.stopPropagation();
              void handleSignCancellation();
            }}
            variant="btn-info"
          >
            <Icon.SendX className="icon-red" />
          </Button>
        </OverlayTrigger>
      )}
      {!(canSignCancellation && cancelStatusP1 === SimP1StatusEnum.ToSign) && (
        <SimP1StatusBadge statusP1={statusP1} cancelStatusP1={cancelStatusP1} />
      )}
      {signStatus === SignatureStatus.ToSign && !props.sendToP1 && (
        <OverlayTrigger
          overlay={<Tooltip style={{ position: 'fixed' }}>Podpisz</Tooltip>}
        >
          <Button
            onClick={e => {
              e.stopPropagation();
              void handleClick();
            }}
            variant="btn-info"
          >
            <Icon.PencilSquare className="icon-red" />
          </Button>
        </OverlayTrigger>
      )}
      {signStatus === SignatureStatus.Signed && statusP1 === null && (
        <OverlayTrigger
          overlay={<Tooltip style={{ position: 'fixed' }}>Podpisane</Tooltip>}
        >
          <Icon.CheckSquare className="icon-green mt-2" />
        </OverlayTrigger>
      )}
    </div>
  );
};
